/**
 * KinsCircle Upload Reliability Layer V1 — Feature Flag
 *
 * Single source of truth for `ENABLE_UPLOAD_RELIABILITY`. Defaults to FALSE
 * so the new queue manager, recovery card, and admin diagnostics card are
 * invisible and inert in production unless an operator explicitly enables
 * them. The locked legacy upload pipelines (MediaUploadModal, PhotoUpload,
 * EditProfileModal, FamilyContext.uploadFamilyPhoto, MessageBoard upload)
 * continue to function 100% as-is regardless of this flag.
 *
 * Resolution order (first match wins):
 *   1. window.__KINSCIRCLE_UPLOAD_RELIABILITY__ === true            (runtime override)
 *   2. localStorage 'kinscircle.upload.reliability.enabled' === '1' (persistent override)
 *   3. import.meta.env.VITE_ENABLE_UPLOAD_RELIABILITY               (build-time)
 *   4. false                                                        (default)
 *
 * Hard contract — strictly additive:
 *   - This module never mutates anything.
 *   - When the flag is FALSE, every reliability component must render
 *     null and every helper must early-return without side effects.
 *   - The new queue manager is OPT-IN: callers explicitly call
 *     `enqueueUpload()` from `uploadQueue.ts`. The legacy MediaUploadModal
 *     keeps its existing direct `supabase.storage.from(...).upload()`
 *     pipeline unchanged.
 *   - Diagnostics surfaces are owner / admin only — never visible to
 *     normal users.
 *
 * Honors STABILITY.md §1, §3, §4 (rollback safety), §7.5 (existing upload
 * resilience contract — never weakened), and §10 (this layer's lock).
 */

const FLAG_LS_KEY = 'kinscircle.upload.reliability.enabled';
const ADMIN_LS_KEY = 'kinscircle.upload.reliability.admin';

const readLocal = (key: string): boolean => {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  try {
    return window.localStorage.getItem(key) === '1';
  } catch {
    return false;
  }
};

const readEnv = (key: string): boolean => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const env = (import.meta as any)?.env ?? {};
    const val = env[key];
    if (typeof val === 'string') {
      return val === 'true' || val === '1';
    }
    if (typeof val === 'boolean') return val;
  } catch {
    /* non-fatal — running in a context without import.meta */
  }
  return false;
};

const readWindow = (key: string): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return !!(window as any)[key];
  } catch {
    return false;
  }
};

/**
 * Master flag for the Upload Reliability Layer V1. When false (default),
 * every reliability component renders null, the queue manager refuses to
 * enqueue, and no behavior changes anywhere in the app.
 */
export const isUploadReliabilityEnabled = (): boolean => {
  if (readWindow('__KINSCIRCLE_UPLOAD_RELIABILITY__')) return true;
  if (readLocal(FLAG_LS_KEY)) return true;
  if (readEnv('VITE_ENABLE_UPLOAD_RELIABILITY')) return true;
  return false;
};

/**
 * Owner / admin gate for the diagnostics card on `/admin/health`. Even
 * when the master flag is enabled, only owners and admins may see the
 * diagnostics surface. Normal users only ever see (when enabled) the
 * reusable `<UploadRecoveryCard />` for their OWN in-flight uploads —
 * never aggregated metrics, never another user's data.
 */
export const canSeeUploadDiagnostics = (
  role: 'owner' | 'admin' | 'member' | 'pending' | string | null | undefined,
): boolean => {
  if (!isUploadReliabilityEnabled()) return false;
  return role === 'owner' || role === 'admin';
};

/** Convenience setter for QA / dev — flips the persistent override. */
export const setUploadReliabilityFlagLocal = (enabled: boolean): void => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    if (enabled) window.localStorage.setItem(FLAG_LS_KEY, '1');
    else window.localStorage.removeItem(FLAG_LS_KEY);
  } catch {
    /* non-fatal */
  }
};

// Re-export so callers can import everything from one place.
export { ADMIN_LS_KEY };
